"use client";

import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Delete, Send } from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Hook to manage visual viewport height for mobile keyboard ergonomics.
 */
export function useViewportHeight() {
  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return;

    const handleResize = () => {
      const vh = window.visualViewport!.height;
      document.documentElement.style.setProperty('--visual-vh', `${vh}px`);
    };

    window.visualViewport.addEventListener('resize', handleResize);
    window.visualViewport.addEventListener('scroll', handleResize);
    handleResize();

    return () => {
      window.visualViewport?.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('scroll', handleResize);
    };
  }, []);
}

interface GameInputProps {
  input: string;
  setInput: React.Dispatch<React.SetStateAction<string>>;
  handleSubmit: (e: React.FormEvent) => void;
  isMyTurn: boolean;
  placeholder: string;
  isNative: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
}

export function GameInput({ 
  input, 
  setInput, 
  handleSubmit, 
  isMyTurn, 
  placeholder, 
  isNative, 
  inputRef 
}: GameInputProps) {
  
  const renderCursor = (isAtStart: boolean) => (
    <motion.span 
      animate={{ opacity: [1, 0] }} 
      transition={{ repeat: Infinity, duration: 0.8 }} 
      className={cn("w-0.5 h-6 md:h-8 bg-pink-500", isAtStart ? "mr-1" : "ml-1")} 
    />
  );

  return (
    <div className="relative w-full group">
      {isNative ? (
        /* NATIVE INPUT MODE */
        <form onSubmit={handleSubmit} className="relative">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value.toUpperCase())}
            disabled={!isMyTurn}
            placeholder={placeholder}
            className={cn(
              "w-full bg-card border-[3px] border-border-main rounded-2xl p-4 md:p-6 pr-16 md:pr-24 text-xl md:text-2xl font-black placeholder:text-slate-300 outline-none handwritten shadow-[4px_4px_0px_0px_var(--border)] transition-all",
              !isMyTurn && "opacity-50 grayscale-[0.5]"
            )}
          />
          <button 
            type="submit"
            disabled={!isMyTurn || !input.trim()}
            className="absolute right-3 top-3 bottom-3 px-4 bg-amber-400 text-white doodle-border-soft shadow-[3px_3px_0px_0px_var(--border)] disabled:bg-slate-300 disabled:shadow-none transition-all boing"
          >
            <Send className="w-6 h-6" />
          </button>
        </form>
      ) : (
        /* VIRTUAL INPUT MODE (Custom Keyboard) */
        <div className="relative">
          <div className={cn(
            "w-full bg-card border-[3px] border-border-main rounded-2xl p-4 md:p-6 pr-16 md:pr-24 text-xl md:text-2xl font-black min-h-[64px] md:min-h-[88px] flex items-center handwritten shadow-[4px_4px_0px_0px_var(--border)] transition-all",
            !isMyTurn && "opacity-50 grayscale-[0.5]"
          )}>
            {isMyTurn && !input && renderCursor(true)}
            {input || <span className="text-slate-300 text-sm md:text-xl truncate">{placeholder}</span>}
            {isMyTurn && input && renderCursor(false)}
          </div>
          <button 
            onClick={(e) => handleSubmit(e as any)}
            disabled={!isMyTurn || !input.trim()}
            className="absolute right-3 top-3 bottom-3 px-4 bg-amber-400 text-white doodle-border-soft shadow-[3px_3px_0px_0px_var(--border)] disabled:bg-slate-300 disabled:shadow-none transition-all boing"
          >
            <Send className="w-6 h-6" />
          </button>
        </div>
      )}
    </div>
  );
}

interface KeyboardGridProps {
  input: string;
  setInput: React.Dispatch<React.SetStateAction<string>>;
  handleSubmit: (e: React.FormEvent) => void;
  isMyTurn: boolean;
}

export function GameKeyboardGrid({ input, setInput, handleSubmit, isMyTurn }: KeyboardGridProps) {
  const ROWS = [
    ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
    ['CLEAR', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', 'BACKSPACE']
  ];

  // Physical Keyboard Bridge
  useEffect(() => {
    if (!isMyTurn) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore key events if a modal is open or if user is in an input elsewhere
      if (document.activeElement?.tagName === 'INPUT' && (document.activeElement as HTMLInputElement).type === 'text') {
        return;
      }

      // POWER-USER FEATURE: Ctrl+Backspace or Cmd+Backspace clears the entire input
      if ((e.ctrlKey || e.metaKey) && e.key === 'Backspace') {
        e.preventDefault();
        setInput('');
        return;
      }

      // WORLD-CLASS FIX: Ignore keystrokes if Ctrl, Alt, or Meta (Command) are pressed
      // This allows standard shortcuts like Ctrl+R, Cmd+L, Alt+Tab to work without typing
      if (e.ctrlKey || e.altKey || e.metaKey) {
        return;
      }

      const key = e.key.toUpperCase();
      if (/^[A-Z]$/.test(key)) setInput(prev => prev + key);
      else if (e.key === 'Backspace') setInput(prev => prev.slice(0, -1));
      else if (e.key === 'Enter') if (input.trim()) handleSubmit(e as any);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [input, setInput, handleSubmit, isMyTurn]);

  const handleKeyClick = (key: string) => {
    if (!isMyTurn) return;

    // Haptic Feedback (Subtle vibration if supported)
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate(10); 
    }
    
    if (key === 'BACKSPACE') {
      setInput(prev => prev.slice(0, -1));
    } else if (key === 'CLEAR') {
      setInput('');
    } else {
      setInput(prev => prev + key);
    }
  };

  return (
    <div className="w-full flex flex-col gap-1.5 md:gap-3 select-none mt-2 md:mt-4 touch-none overscroll-none">
      {ROWS.map((row, rowIndex) => (
        <div key={rowIndex} className="flex justify-center gap-1 md:gap-2 touch-none">
          {rowIndex === 1 && <div className="flex-[0.5]" />}
          {row.map((key) => {
            const isControl = key === 'BACKSPACE' || key === 'CLEAR';
            return (
              <button
                key={key}
                onPointerDown={(e) => {
                  e.preventDefault();
                  handleKeyClick(key);
                }}
                // Prevent context menu on long press
                onContextMenu={(e) => e.preventDefault()}
                disabled={!isMyTurn}
                className={cn(
                  "h-12 md:h-16 rounded-lg md:rounded-xl border-2 border-[var(--border)] font-black flex items-center justify-center transition-all uppercase select-none touch-none",
                  "active:translate-y-[2px] active:shadow-none", // Physical press feel
                  !isControl && "flex-1 min-w-[28px] max-w-[45px] md:max-w-[60px] bg-card text-[var(--border)] shadow-[0px_3px_0px_0px_var(--border)] text-sm md:text-xl active:bg-muted",
                  key === 'CLEAR' && "flex-[1.5] px-1 md:px-4 bg-rose-100 text-rose-500 shadow-[0px_3px_0px_0px_#F43F5E] text-[10px] md:text-xs active:bg-rose-200",
                  key === 'BACKSPACE' && "flex-[1.5] px-1 md:px-4 bg-rose-100 text-rose-500 shadow-[0px_3px_0px_0px_#F43F5E] active:bg-rose-200"
                )}
              >
                {key === 'BACKSPACE' ? <Delete className="w-5 h-5 md:w-6 md:h-6" /> : key}
              </button>
            );
          })}

          {rowIndex === 1 && <div className="flex-[0.5]" />}
        </div>
      ))}
    </div>
  );
}
